import React, { Suspense, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'gatsby';
import { Container } from '@chakra-ui/react';

import Breadcrumb, {
  BreadCrumbShape,
} from '@rsc/ui-components/dept/molecules/Breadcrumb';
import Blocks from '@rsc/ui-components/dept/organisms/Blocks/Blocks';
import ConvertAB from '@rsc/ui-components/tailwind/ConvertAB';

import Layout from '@components/layout';
import createOpenGraphTags from '@rsc/utils/createOpenGraphTags';
import { usePetsafeInternationalContext } from '@util/static-translations';
import useSiteSettingsContext from '@components/hooks/useSiteSettingsContext';
import { getLocalizedPagePath } from '@rsc/gatsby-localization-helpers';

export default function CollectionPage({ data, location, pageContext }) {
  const { collection } = data;
  const { linkHreflangValues } = pageContext;

  const [isBrowser, setIsBrowser] = useState(false);
  const { tt, locale } = usePetsafeInternationalContext();
  const { siteSettings } = useSiteSettingsContext();

  useEffect(() => {
    if (typeof window !== `undefined`) {
      setIsBrowser(true);
    }
  }, []);

  const collectionTitle = collection?.store?.title.split('/').at(-1);
  const localizedCollectionTitle =
    collectionTitle.toLowerCase() === 'all products'
      ? tt(s => s.collectionsPage.allProductsCollectionTitleLabel)
      : collectionTitle;
  const seoDescription = tt(s => s.collectionsPage.seoDescriptionLabel);

  // filter out products not in the lifecycle array from collection
  const acceptedLifecycle = ['Available', 'Coming Soon', 'Discontinued'];
  const filteredCollection = data?.collection?.products.filter(product =>
    acceptedLifecycle.includes(product?.shopifyProduct?.productLifecycle)
  );
  // eslint-disable-next-line no-param-reassign
  data.collection.products = filteredCollection;

  return (
    <>
      <ConvertAB
        pageType="collection"
        categoryName={collection?.store?.title}
      />
      <Layout
        headTagData={{
          title:
            collection?.SEOMetadata?.title ||
            tt(s => s.commonLabels.petsafeTitleLabel, {
              title: localizedCollectionTitle,
            }),
          description: collection?.SEOMetadata?.description || seoDescription,
          canonicalUrl: collection?.SEOMetadata?.canonicalUrl,
          noIndex: collection?.SEOMetadata?.noIndex,
          noFollow: collection?.SEOMetadata?.noFollow,
          includeBVScript: true,
          enableKlaviyo: true,
          meta: createOpenGraphTags(
            {
              title:
                collection?.SEOMetadata?.ogTitle ||
                collection?.SEOMetadata?.title ||
                collectionTitle,
              description:
                collection?.SEOMetadata?.ogDescription ||
                collection?.SEOMetadata?.description ||
                seoDescription,
              url: getLocalizedPagePath(
                collection?.SEOMetadata?.ogUrl ||
                  (isBrowser && window?.location?.pathname) ||
                  '/',
                locale
              ),
              image:
                collection?.SEOMetadata?.ogImage ||
                collection?.image ||
                collection.products?.[0]?.store?.previewImageUrl ||
                '',
            },
            'petsafe'
          ),
        }}
        linkHreflangValues={linkHreflangValues}
      >
        <Container>
          <Breadcrumb breadcrumbs={pageContext.breadcrumbs} />
        </Container>
        {/* If there aren't any blocks, just render the product collection grid */}
        <Suspense fallback={null}>
          {collection.blocks?.blocks?.length ? (
            <Blocks
              blocks={collection.blocks.blocks}
              data={data}
              pageContext={pageContext}
              location={location}
              siteSettings={siteSettings}
            />
          ) : (
            <Blocks
              blocks={[
                {
                  _key: 'default_collection_block_hero_key',
                  _type: 'deptTextHeroesDefault',
                  headline:
                    localizedCollectionTitle ||
                    tt(s => s.collectionsPage.defaultCollectionTitleLabel),
                },
                {
                  _key: 'default_collection_block_grid_key',
                  _type: 'deptCollectionGrid',
                },
              ]}
              data={data}
              pageContext={pageContext}
              location={location}
              siteSettings={siteSettings}
            />
          )}
        </Suspense>
      </Layout>
    </>
  );
}

CollectionPage.propTypes = {
  pageContext: PropTypes.shape({
    breadcrumbs: PropTypes.arrayOf(BreadCrumbShape),
    linkHreflangValues: {},
  }).isRequired, // consider using shape() if you know the properties.
  location: PropTypes.shape({}).isRequired, // consider using shape() if you know the properties.
  data: PropTypes.shape({
    collection: PropTypes.shape({
      SEOMetadata: PropTypes.shape({
        title: PropTypes.string,
        description: PropTypes.string,
        canonicalUrl: PropTypes.string,
        noIndex: PropTypes.bool,
        noFollow: PropTypes.bool,
        ogTitle: PropTypes.string,
        ogDescription: PropTypes.string,
        ogUrl: PropTypes.string,
        ogImage: PropTypes.string,
      }),
      store: PropTypes.shape({
        title: PropTypes.string,
      }),
      description: PropTypes.string,
      image: PropTypes.shape({}),
      products: PropTypes.arrayOf(
        PropTypes.shape({
          store: PropTypes.shape({
            previewImageUrl: PropTypes.string,
          }),
        })
      ),
      blocks: PropTypes.shape({
        blocks: PropTypes.arrayOf(
          PropTypes.shape({
            _key: PropTypes.string,
            _type: PropTypes.string,
            headline: PropTypes.string,
            title: PropTypes.string,
          })
        ),
      }),
    }),
  }).isRequired,
};

export const query = graphql`
  fragment SiteSettingsFields on SanitySiteSettings {
    contactEmail
    deliveryTimeEstimate {
      minDeliveryDays
      maxDeliveryDays
    }
    shipping
    returnDays
    newsletter {
      newsletterDescription
      newsletterTitle
    }
    socialMedia {
      facebookBool
      facebookUrl
      instagramBool
      instagramUrl
      youtubeBool
      youtubeUrl
      twitterBool
      twitterUrl
      pinterestBool
      pinterestUrl
      tiktokBool
      tiktokUrl
    }
  }

  query collectionPageTree($collectionId: String) {
    collection: sanityGlobalProductCollection(_id: { eq: $collectionId }) {
      _id
      description
      SEOMetadata {
        title
        description
        canonicalUrl
        noIndex
        noFollow
        ogTitle
        ogDescription
        ogUrl
        ogImage {
          asset {
            _id
            url
          }
        }
      }
      store {
        title
        slug {
          current
        }
      }
      blocks {
        ...DeptBlockFields
      }
      products {
        ...SanityGlobalProductFields
        _id
        dynamicFeatures {
          text
        }
        badge {
          hex
          text
        }
        store {
          previewImageUrl
          variants {
            sku
            metafields {
              name
              value
            }
          }
        }
      }
    }
  }
`;
